import React from 'react';
import InputTextArea from 'ui/InputTextArea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel } from 'ui/InputSelect';
import { PrivacyLevel } from 'util/userUtils';
import DetailPanel from 'ui/DetailPanel';
import { Progress } from 'ui/ProgressBar';
import { ToggleGroup, ToggleGroupItem } from 'ui/ToggleGroup';
import { cn } from 'util/cn';
import ActorPrivacy from './ActorPrivacy';
import { ViewerRelation } from 'util/constants';

interface AlignmentFormProps {
    statement: string;
    statements: string[];
    currentStatementIndex: number;
    response: number;
    importance: number;
    note: string;
    privacy: PrivacyLevel;
    onResponseChange: (value: number) => void;
    onImportanceChange: (value: number) => void;
    onNoteChange: (value: string) => void;
    onPrivacyChange: (value: PrivacyLevel) => void;
    onStatementChange: (index: number) => void;
    isActorPrivate?: boolean;
    isPanelOpen?: boolean;
    onPanelOpenChange?: (open: boolean) => void;
    actorType: 'user' | 'team';
    viewerRelation?: ViewerRelation;
    allAnswers?: {
        id: number;
        response: number;
        importance: number;
        note: string;
        privacy: PrivacyLevel;
        created_at: string;
        updated_at: string;
    }[];
}

const AlignmentForm: React.FC<AlignmentFormProps> = ({
    statement,
    statements,
    currentStatementIndex,
    response,
    importance,
    note,
    privacy,
    onResponseChange,
    onImportanceChange,
    onNoteChange,
    onPrivacyChange,
    onStatementChange,
    isActorPrivate = false,
    isPanelOpen = false,
    onPanelOpenChange,
    actorType = 'user',
    viewerRelation,
    allAnswers = []
}) => {
    const responseOptions = [
        { value: -2, label: "Strongly Disagree" },
        { value: -1, label: "Disagree" },
        { value: 0, label: "Neutral" },
        { value: 1, label: "Agree" },
        { value: 2, label: "Strongly Agree" },
    ];

    const importanceOptions = [
        { value: -2, label: "Very Unimportant" },
        { value: -1, label: "Unimportant" },
        { value: 0, label: "Neutral" },
        { value: 1, label: "Important" },
        { value: 2, label: "Very Important" },
    ];

    // Handle statement change
    const handleStatementChange = (statementIndex: string) => {
        onStatementChange(parseInt(statementIndex));
    };

    // Wrapper for ActorPrivacy's onPrivacyChange
    const handlePrivacyChange = (newPrivacy: PrivacyLevel, settingKey: string) => {
        onPrivacyChange(newPrivacy);
    };

    return (
        <div className="flex flex-col">
            {/* Statement Selection - Always visible */}
            <div className="sticky top-0 bg-eoe-bg dark:bg-eoe-dark-bg-modal pb-4">
                <div className="mb-2">
                    <div className="flex flex-col gap-1">
                        <Progress 
                            value={((currentStatementIndex + 1) / statements.length) * 100}
                            className="relative"
                        >
                            <span className="absolute inset-0 flex items-center justify-center text-xs font-medium text-eoe-text-meta dark:text-eoe-dark-text-sub z-10">
                                {currentStatementIndex + 1}/{statements.length}
                            </span>
                        </Progress>
                    </div>
                    <Select 
                        value={currentStatementIndex.toString()}
                        onValueChange={handleStatementChange}
                        required={true}
                    >
                        <SelectTrigger className="input-select text-base px-3 py-2 text-center font-medium text-eoe-text dark:text-eoe-dark-text mt-2">
                            <SelectValue placeholder="Select statement">{statement}</SelectValue>
                        </SelectTrigger>
                        <SelectContent className="z-[2000] sm:max-w-[400px] border border-eoe-border-main dark:border-eoe-dark-border-main">
                            <SelectGroup>
                                <SelectLabel>Unanswered</SelectLabel>
                                {statements.map((stmt, index) => {
                                    // Check if this statement has a response
                                    const answer = allAnswers[index];
                                    const isAnswered = answer && answer.response !== null;
                                    
                                    // Skip if answered
                                    if (isAnswered) return null;
                                    
                                    return (
                                        <SelectItem 
                                            key={`unanswered-${index}`} 
                                            value={index.toString()} 
                                            className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub"
                                        >
                                            {index + 1}. {stmt}
                                        </SelectItem>
                                    );
                                })}
                            </SelectGroup>
                            
                            <SelectGroup>
                                <SelectLabel>Answered</SelectLabel>
                                {statements.map((stmt, index) => {
                                    // Check if this statement has a response
                                    const answer = allAnswers[index];
                                    const isAnswered = answer && answer.response !== null;
                                    
                                    // Skip if not answered
                                    if (!isAnswered) return null;
                                    
                                    return (
                                        <SelectItem 
                                            key={`answered-${index}`} 
                                            value={index.toString()} 
                                            className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub"
                                        >
                                            {index + 1}. {stmt}
                                        </SelectItem>
                                    );
                                })}
                            </SelectGroup>
                        </SelectContent>
                    </Select>
                </div>
            
                {/* Response Buttons - Replace with ToggleGroup */}
                <div className="mt-4">
                    <div className="flex justify-between items-center mb-1">
                        <p className="text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub">Alignment</p>
                    </div>
                    <ToggleGroup 
                        type="single" 
                        value={response !== null && response !== undefined ? response.toString() : "0"} 
                        onValueChange={(value) => onResponseChange(parseInt(value))}
                        className="justify-between w-full [&>*:first-child]:rounded-l-2xl [&>*:last-child]:rounded-r-2xl"
                    >
                        {responseOptions.map((option) => (
                            <ToggleGroupItem
                                key={option.value}
                                value={option.value.toString()}
                                className={cn(
                                    "flex-1 text-[9px] sm:text-xxs md:text-xs font-light data-[state=on]:text-[9px] bg-eoe-icon dark:bg-eoe-dark-icon rounded-none",
                                    (response !== null && response !== undefined && response === option.value) && "bg-eoe-blue dark:bg-eoe-blue hover:bg-eoe-blue dark:hover:bg-eoe-blue hover:text-white dark:hover:text-eoe-dark-text text-white dark:text-eoe-dark-text"
                                )}
                                aria-label={option.label}
                            >
                                {option.value === -2 && "Strongly Disagree"}
                                {option.value === -1 && "Disagree"}
                                {option.value === 0 && "Neutral"}
                                {option.value === 1 && "Agree"}
                                {option.value === 2 && "Strongly Agree"}
                            </ToggleGroupItem>
                        ))}
                    </ToggleGroup>
                </div>
                
                {/* Importance Buttons - Replace with ToggleGroup */}
                <div className="mt-4">
                    <div className="flex justify-between items-center mb-1">
                        <p className="text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub">Importance</p>
                    </div>
                    <ToggleGroup 
                        type="single" 
                        value={importance !== null && importance !== undefined ? importance.toString() : "0"} 
                        onValueChange={(value) => onImportanceChange(parseInt(value))}
                        className="justify-between w-full [&>*:first-child]:rounded-l-2xl [&>*:last-child]:rounded-r-2xl"
                    >
                        {importanceOptions.map((option) => (
                            <ToggleGroupItem
                                key={option.value}
                                value={option.value.toString()}
                                className={cn(
                                    "flex-1 text-[9px] sm:text-xxs md:text-xs font-light data-[state=on]:text-[9px] bg-eoe-icon dark:bg-eoe-dark-icon rounded-none",
                                    (importance !== null && importance !== undefined && importance === option.value) && "bg-eoe-blue dark:bg-eoe-blue hover:bg-eoe-blue dark:hover:bg-eoe-blue hover:text-white dark:hover:text-eoe-dark-text text-white dark:text-eoe-dark-text"
                                )}
                                aria-label={option.label}
                            >
                                {option.value === -2 && "Very Unimportant"}
                                {option.value === -1 && "Unimportant"}
                                {option.value === 0 && "Neutral"}
                                {option.value === 1 && "Important"}
                                {option.value === 2 && "Very Important"}
                            </ToggleGroupItem>
                        ))}
                    </ToggleGroup>
                </div>
            </div>

            {/* Remaining form elements - Can be scrolled if needed */}
            <DetailPanel 
                title={`Settings`}
                open={isPanelOpen}
                onOpenChange={onPanelOpenChange}
                className="text-sm bg-eoe-bg-modal-hover dark:bg-eoe-dark-bg-modal-hover mb-4">
                <div className="space-y-4">
                    {/* Privacy Setting using ActorPrivacy component */}
                    <div className="space-y-2">
                        <ActorPrivacy 
                            privacy={privacy}
                            settingKey={`alignment_answer_${currentStatementIndex + 1}`}
                            onPrivacyChange={handlePrivacyChange}
                            actorType={actorType}
                            editMode={true}
                            showDropdown={true}
                            miniIcon={false}
                            viewerRelation={viewerRelation}
                        />
                    </div>

                    {/* Note TextArea */}
                    <div className="form-control mb-4">
                        <InputTextArea
                            id="alignment-note"
                            name="alignment-note"
                            labelText="Note (optional)"
                            value={note}
                            fieldPlaceholder="Add context/clarification for others"
                            onChange={(e) => onNoteChange(e.target.value)}
                            spellCheck={true}
                            rows={2}
                            maxLength={500}
                        />
                    </div>
                </div>
            </DetailPanel>
        </div>
    );
};

export default AlignmentForm;
